import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { AuthProvider } from '@aimsos/auth';
import { ThemeProvider } from '@aimsos/theme';
import { ToastProvider } from '@aimsos/ui';
import { I18nProvider } from '@aimsos/i18n';
import { i18n } from './i18n/bootstrap.js';
import App from './App.jsx';
import auth0Config from './config/auth0Config.js';
import './index.css';

/**
 * Provides AuthProvider with access to React Router's navigate for onRedirectCallback.
 * Must be rendered inside BrowserRouter.
 *
 * Auth0 is only ever invoked for the one-time identity login now — moving
 * between workspace subdomains or switching workspaces afterwards hands our
 * own backend JWT directly to the destination origin instead (see
 * @aimsos/auth's tokenHandoff.js + WorkspaceGate), so onRedirectCallback no
 * longer needs to plumb any workspace-targeting state through Auth0's
 * appState.
 */
const AuthWrapper = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname, { replace: true });
  };

  return (
    <AuthProvider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      audience={auth0Config.audience}
      redirectUri={auth0Config.redirectUri}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </AuthProvider>
  );
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <I18nProvider i18n={i18n}>
      <ThemeProvider>
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <AuthWrapper>
            <ToastProvider>
              <App />
            </ToastProvider>
          </AuthWrapper>
        </BrowserRouter>
      </ThemeProvider>
    </I18nProvider>
  </React.StrictMode>
);
